import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { XIcon, SaveIcon } from 'lucide-react';
import SuccessDialog from '../DialogHandling/SuccessDialog';
import ErrorDialog from '../DialogHandling/ErrorDialog';

const EditAdminForm = ({ admin, setEditingAdmin, handleEdit }) => {
  const [formData, setFormData] = useState({
    name: admin ? admin.name : '',
    phone: admin ? admin.phone : ''
  });

  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    // Update form state when admin prop changes
    if (admin) {
      setFormData({
        name: admin.name || '',
        phone: admin.phone || ''
      });
    }
  }, [admin]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await handleEdit(admin.id, formData);
      setShowSuccessDialog(true);
    } catch (error) {
      console.error("Error updating Admin:", error);
      setErrorMessage("Failed to update admin. Please try again.");
      setShowErrorDialog(true);
    }
  };

  const handleCloseForm = () => {
    setEditingAdmin(null); // Close the form without saving changes
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-green-500/50 to-green-200/50 backdrop-blur-sm flex justify-center items-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-2xl max-w-md w-full"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <h2 className="text-3xl font-bold mb-6 text-gray-800 dark:text-white text-center">
            Edit Admin
          </h2>
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-green-500 dark:focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition duration-200"
              required
            />
          </div>
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Phone
            </label>
            <input
              type="text"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-green-500 dark:focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition duration-200"
              required
            />
          </div>
          <div className="flex items-center justify-between pt-4">
            <button
              type="submit"
              className="flex items-center px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
            >
              <SaveIcon className="w-5 h-5 mr-2" />
              Save Changes
            </button>
            <button
              onClick={handleCloseForm}
              type="button"
              className="flex items-center px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-lg transition duration-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 dark:focus:ring-gray-500 dark:focus:ring-offset-gray-800"
            >
              <XIcon className="w-5 h-5 mr-2" />
              Cancel
            </button>
          </div>
        </form>
        {showSuccessDialog && (
          <SuccessDialog
            message="Admin updated successfully!"
            onClose={() => setShowSuccessDialog(false)}
          />
        )}
        {showErrorDialog && (
          <ErrorDialog
            message={errorMessage}
            onClose={() => setShowErrorDialog(false)}
          />
        )}
      </motion.div>
    </div>
  );
};

export default EditAdminForm;
